import { Card, Box, Typography } from '@mui/material';
import { getTeamImage } from '../../../models/TeamConstants';
import { getCorrectForPeriod, type LeaderboardPeriod } from '../../../hooks/useLeaderboardData';
import { groupAchievementsForPeriod } from '../../achievements/achievementHelpers';
import { useAchievements } from '../../../hooks/useAchievements';
import AchievementBadgeRow from '../../achievements/components/AchievementBadgeRow';
import type { User } from '../../../models/User';

interface LeaderboardPlayerCardProps {
  player: User;
  playerRank: number;
  activePeriod: LeaderboardPeriod;
}

const STAT_LABEL_COLOR = 'rgba(206,147,216,0.75)';
const STAT_VALUE_COLOR = '#dda0f0';
const ACHIEVEMENTS_LABEL_COLOR = 'rgba(206,147,216,0.45)';

export default function LeaderboardPlayerCard({ player, playerRank, activePeriod }: LeaderboardPlayerCardProps) {
  // Derive correct answers and points from the active period — eliminates the
  // prior mismatch risk of passing playerScore down from the parent as totalCorrectAnswers * 10
  const correctAnswers = getCorrectForPeriod(player, activePeriod);
  const playerScore = correctAnswers * 10;

  const { data: achievements } = useAchievements(player.userId);
  const badgeGroups = groupAchievementsForPeriod(achievements ?? [], activePeriod);

  return (
    <Card sx={{
      mb: 1,
      p: 2.5,
      backgroundColor: '#27172c',
      border: '0.5px solid rgba(206,147,216,0.25)',
    }}>
      {/* Header row */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Box sx={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'rgba(186,104,200,0.18)',
          border: '1.5px solid rgba(206,147,216,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.75rem', flexShrink: 0,
        }}>
          {getTeamImage(player.team)}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 500, color: '#e1bee7', lineHeight: 1.2 }}>
            {player.username}
          </Typography>
          <Typography sx={{ fontSize: 16, color: 'rgba(206,147,216,0.6)' }}>
            Team {player.team || 'Unsorted'}
          </Typography>
        </Box>
      </Box>

      {/* Stat chips — vertical padding reduced by 50% (10px → 5px) */}
      <Box display="flex" gap={1.25}>
        {[
          { label: 'Rank', value: `#${playerRank}` },
          { label: 'Points', value: playerScore },
          { label: 'Correct', value: correctAnswers },
        ].map(({ label, value }) => (
          <Box key={label} sx={{
            flex: 1,
            background: 'rgba(255,255,255,0.05)',
            border: '0.5px solid rgba(206,147,216,0.15)',
            borderRadius: 2,
            px: '12px',
            py: '5px',
          }}>
            <Typography sx={{ fontSize: 13, color: STAT_LABEL_COLOR, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>
              {label}
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 500, color: STAT_VALUE_COLOR }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Achievement badges — only shown when there are badges for the active period */}
      {badgeGroups.length > 0 && (
        <Box mt={1.5}>
          {/* Subtle section label */}
          <Typography sx={{
            fontSize: 13,
            color: ACHIEVEMENTS_LABEL_COLOR,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 1,
          }}>
            Achievements
          </Typography>
          <AchievementBadgeRow badgeGroups={badgeGroups} justify="flex-start" />
        </Box>
      )}
    </Card>
  );
}